import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Instagram, Send, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import WhatsAppButton from '@/components/WhatsAppButton';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/treatments', label: 'Treatments' },
    { path: '/faq', label: 'FAQ' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact Us' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="w-20 h-20 rounded-full overflow-hidden">
                <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${isActive(link.path) ? 'text-slate-900 underline underline-offset-4' : 'text-slate-800 hover:text-slate-900'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <a href="tel:0614481090" className="hidden md:flex items-center gap-2 text-sm text-slate-800 hover:text-slate-900 transition-colors">
                <Phone className="w-4 h-4" />
                <span>061 448 1090</span>
              </a>
              
              <Link to={user ? '/admin' : '/login'} className="text-sm font-medium text-slate-800 hover:text-slate-900 transition-colors">
                {user ? 'Dashboard' : 'Login'}
              </Link>

              <button className="lg:hidden p-2 text-slate-800" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#5ce1e6] border-t border-[#22d3ee]">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 px-3 rounded-lg text-sm ${isActive(link.path) ? 'bg-white/20 text-slate-900' : 'text-slate-800 hover:bg-white/10'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section - White Background */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 animate-fade-in" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Get In Touch
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              Book your appointment or ask us any questions
            </p>
            <Link 
              to="/book" 
              className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-[#5ce1e6] text-slate-900 rounded-full font-medium hover:bg-[#22d3ee] transition-colors"
              data-testid="contact-book-btn"
            >
              Book Appointment
            </Link>
          </div>
        </section>

        {/* Contact Info & Form - Side by Side */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Contact Information - Turquoise Background */}
              <div className="bg-[#5ce1e6] p-8 lg:p-12 rounded-l-2xl lg:rounded-l-2xl rounded-t-2xl lg:rounded-t-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Information
                </h2>
                
                <div className="space-y-5">
                  <a href="tel:0614481090" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Telephone</p>
                      <p className="text-slate-700">061 448 1090</p>
                    </div>
                  </a>

                  <a href="https://wa.me/27659972538?text=Hi%20Bong,%20I'd%20like%20to%20book%20a%20physiotherapy%20appointment." target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Cellphone / WhatsApp</p>
                      <p className="text-slate-700">065 997 2538</p>
                    </div>
                  </a>

                  <a href="mailto:bnphysio0@gmail.com" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Email</p>
                      <p className="text-slate-700">bnphysio0@gmail.com</p>
                    </div>
                  </a>

                  <a href="https://www.instagram.com/bn_physiotherapy?igsh=em1jM3o5bzBwZ3g=" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Instagram</p>
                      <p className="text-slate-700">@bn_physiotherapy</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Location</p>
                      <p className="text-slate-700">KT Health & Wellness Medical Center</p>
                      <p className="text-slate-700">Upper level, Cedar Square, Fourways</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Home Visits</p>
                      <p className="text-slate-700">Johannesburg North and West Rand area</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#0e7490]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Operating Hours</p>
                      <p className="text-slate-700">Monday - Friday: 09:00 - 18:00</p>
                      <p className="text-slate-700">Saturday: 09:00 - 13:00</p>
                      <p className="text-slate-700">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form - White Background */}
              <div className="bg-white p-8 lg:p-12 rounded-r-2xl lg:rounded-r-2xl rounded-b-2xl lg:rounded-b-none shadow-sm border border-slate-100">
                <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Send Us a Message
                </h2>
                
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Your phone number"
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="How can we help you?"
                        rows={4}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="map-container rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3584.2!2d28.0126!3d-26.0178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e957359bfc6b5f5%3A0x8b3c57c7c7c7c7c7!2sCedar%20Square%20Shopping%20Centre%2C%20Cnr%20Cedar%20Rd%20%26%20Witkoppen%20Rd%2C%20Fourways%2C%20Sandton%2C%202055!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BN Physiotherapy - Cedar Square, Fourways"
              />
            </div>
            <p className="text-center text-slate-600 mt-4">
              <strong>📍 KT Health & Wellness Medical Center</strong> - Upper level, Cedar Square, Fourways
            </p>
          </div>
        </section>
      </main>

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
}
